import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BrandingForm = ({ onSubmit, onCalculate, calculationData, isCalculating }) => {
  const [formData, setFormData] = useState({
    storeId: '',
    brandName: '',
    brandingMedium: '',
    kvId: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [generatedBrandingId, setGeneratedBrandingId] = useState('');

  // Mock data for stores with location codes
  const stores = [
    { value: 'store-001', label: 'Dhaka Central Mall - Store A1', locationCode: 'DHA', rent: 45000, size: 800 },
    { value: 'store-002', label: 'Chittagong Plaza - Store B2', locationCode: 'CTG', rent: 35000, size: 650 },
    { value: 'store-003', label: 'Sylhet Shopping Center - Store C3', locationCode: 'SYL', rent: 28000, size: 500 },
    { value: 'store-004', label: 'Rajshahi Market - Store D4', locationCode: 'RAJ', rent: 22000, size: 400 },
    { value: 'store-005', label: 'Khulna Complex - Store E5', locationCode: 'KHU', rent: 30000, size: 550 }
  ];

  // Mock data for brands with brand codes
  const brands = [
    { value: 'coca-cola', label: 'Coca-Cola', code: 'COC' },
    { value: 'pepsi', label: 'Pepsi', code: 'PEP' },
    { value: 'samsung', label: 'Samsung', code: 'SAM' },
    { value: 'lg', label: 'LG Electronics', code: 'LGE' },
    { value: 'unilever', label: 'Unilever', code: 'UNI' },
    { value: 'lily-skincare', label: 'Lily Skincare', code: 'LIS' },
    { value: 'cavotin-haircare', label: 'Cavotin Haircare', code: 'CAV' },
    { value: 'nestle', label: 'Nestlé', code: 'NES' },
    { value: 'grameenphone', label: 'Grameenphone', code: 'GRM' },
    { value: 'robi', label: 'Robi Axiata', code: 'ROB' }
  ];

  // Updated branding mediums with proper codes
  const brandingMediums = [
    { value: 'window-display', label: 'Window Display', code: 'WD', baseRate: 2500 },
    { value: 'billboard', label: 'Billboard', code: 'BB', baseRate: 8000 },
    { value: 'led-screen', label: 'LED Screen', code: 'LS', baseRate: 15000 },
    { value: 'in-store-static', label: 'In-Store Static', code: 'IS', baseRate: 1200 },
    { value: 'gondola-branding', label: 'Gondola Branding', code: 'GB', baseRate: 3500 },
    { value: 'fsu', label: 'FSU', code: 'FSU', baseRate: 5500 }
  ];

  // Mock KV data
  const keyVisuals = [
    { value: 'kv-001', label: 'KV001 - Summer Campaign 2024', dimensions: '1920x1080', area: 12.5 },
    { value: 'kv-002', label: 'KV002 - Product Launch Banner', dimensions: '1080x1920', area: 8.2 },
    { value: 'kv-003', label: 'KV003 - Holiday Special', dimensions: '2400x1600', area: 15.0 },
    { value: 'kv-004', label: 'KV004 - Brand Awareness', dimensions: '1600x900', area: 10.8 },
    { value: 'kv-005', label: 'KV005 - Store Opening', dimensions: '3000x2000', area: 20.0 }
  ];

  // Generate Branding ID function
  const generateBrandingId = (storeId, brandName, mediumValue) => {
    const selectedStore = stores?.find(s => s?.value === storeId);
    const selectedBrand = brands?.find(b => b?.value === brandName);
    const selectedMedium = brandingMediums?.find(m => m?.value === mediumValue);

    if (selectedStore && selectedBrand && selectedMedium) {
      const mediumCode = selectedMedium?.code;
      const locationCode = selectedStore?.locationCode;
      const brandCode = selectedBrand?.code;
      
      // Generate sequential number (mock - in real app would be from database)
      const serialNumber = String(Math.floor(Math.random() * 999) + 1)?.padStart(3, '0');
      
      return `${mediumCode}-${locationCode}-${brandCode}-${serialNumber}`;
    }
    return '';
  };

  // Auto-generate Branding ID when required fields are filled
  useEffect(() => {
    if (formData?.storeId && formData?.brandName && formData?.brandingMedium) {
      const brandingId = generateBrandingId(formData?.storeId, formData?.brandName, formData?.brandingMedium);
      setGeneratedBrandingId(brandingId);
    } else {
      setGeneratedBrandingId('');
    }
  }, [formData?.storeId, formData?.brandName, formData?.brandingMedium]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.storeId) {
      newErrors.storeId = 'Please select a store';
    }
    if (!formData?.brandName) {
      newErrors.brandName = 'Please select a brand';
    }
    if (!formData?.brandingMedium) {
      newErrors.brandingMedium = 'Please select a branding medium';
    }
    if (!formData?.kvId) {
      newErrors.kvId = 'Please select a key visual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleCalculate = () => {
    if (validateForm()) {
      const selectedStore = stores?.find(s => s?.value === formData?.storeId);
      const selectedMedium = brandingMediums?.find(m => m?.value === formData?.brandingMedium);
      const selectedBrand = brands?.find(b => b?.value === formData?.brandName);
      const selectedKV = keyVisuals?.find(kv => kv?.value === formData?.kvId);
      
      if (selectedStore && selectedMedium && selectedBrand && selectedKV) {
        const rentPerSqFt = selectedStore?.rent / selectedStore?.size;
        const locationMultiplier = rentPerSqFt > 60 ? 1.3 : rentPerSqFt > 40 ? 1.1 : 1.0;
        const unitRate = Math.round(selectedMedium?.baseRate * locationMultiplier);
        const totalFees = unitRate; // No quantity multiplier, just unit rate

        onCalculate({
          ...formData,
          brandingId: generatedBrandingId,
          storeDetails: selectedStore,
          mediumDetails: selectedMedium,
          brandDetails: selectedBrand,
          kvDetails: selectedKV,
          rentPerSqFt,
          locationMultiplier,
          unitRate,
          totalFees,
          area: selectedKV?.area
        });
      }
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm() && calculationData) {
      onSubmit({
        ...calculationData,
        id: `branding-${Date.now()}`,
        createdAt: new Date(),
        status: 'planned'
      });
      
      // Reset form
      setFormData({
        storeId: '',
        brandName: '',
        brandingMedium: '',
        kvId: '',
        description: ''
      });
      setGeneratedBrandingId('');
    }
  };

  // Auto-calculate when form data changes
  useEffect(() => {
    if (formData?.storeId && formData?.brandingMedium && formData?.kvId && formData?.brandName) {
      handleCalculate();
    }
  }, [formData?.storeId, formData?.brandingMedium, formData?.kvId, formData?.brandName]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Calculator" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Add Branding Item</h2>
          <p className="text-sm text-muted-foreground">Create new branding item with automatic ID generation</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Branding ID Display */}
        {generatedBrandingId && (
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Hash" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Generated Branding ID</span>
            </div>
            <div className="font-mono text-lg font-bold text-primary">{generatedBrandingId}</div>
            <div className="text-xs text-muted-foreground mt-1">
              Format: Medium Code - Location Code - Brand Code - Serial Number
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Store Selection */}
          <Select
            label="Store Location (with Location Code)"
            placeholder="Select store"
            options={stores}
            value={formData?.storeId}
            onChange={(value) => handleInputChange('storeId', value)}
            error={errors?.storeId}
            required
            searchable
          />

          {/* Brand Selection */}
          <Select
            label="Brand (with Brand Code)"
            placeholder="Select brand"
            options={brands}
            value={formData?.brandName}
            onChange={(value) => handleInputChange('brandName', value)}
            error={errors?.brandName}
            required
            searchable
          />

          {/* Branding Medium Selection */}
          <Select
            label="Branding Medium"
            placeholder="Select branding medium"
            options={brandingMediums}
            value={formData?.brandingMedium}
            onChange={(value) => handleInputChange('brandingMedium', value)}
            error={errors?.brandingMedium}
            required
            searchable
          />

          {/* KV Selection */}
          <Select
            label="KV ID (Key Visual)"
            placeholder="Select key visual"
            options={keyVisuals}
            value={formData?.kvId}
            onChange={(value) => handleInputChange('kvId', value)}
            error={errors?.kvId}
            required
            searchable
          />
        </div>

        {/* Auto-calculated area display */}
        {formData?.kvId && (
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="text-sm text-muted-foreground mb-1">Dimensions / Area (Auto-calculated from KV)</div>
            <div className="font-medium text-foreground">
              {keyVisuals?.find(kv => kv?.value === formData?.kvId)?.dimensions} 
              <span className="text-muted-foreground mx-2">•</span>
              {keyVisuals?.find(kv => kv?.value === formData?.kvId)?.area} sqft
            </div>
          </div>
        )}

        {/* Description */}
        <Input
          label="Description (Optional)"
          type="text"
          placeholder="Additional notes or specifications"
          value={formData?.description}
          onChange={(e) => handleInputChange('description', e?.target?.value)}
        />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleCalculate}
            disabled={!formData?.storeId || !formData?.brandingMedium || !formData?.kvId || !formData?.brandName}
            loading={isCalculating}
            iconName="Calculator"
            iconPosition="left"
            className="flex-1 sm:flex-none"
          >
            Calculate Fees
          </Button>
          
          <Button
            type="submit"
            disabled={!calculationData}
            iconName="Plus"
            iconPosition="left"
            className="flex-1 sm:flex-none"
          >
            Add Branding Item
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BrandingForm;